import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import {
  CheckCircle,
  Description,
  Lock,
  Payment,
  FileDownload,
} from "@mui/icons-material";
import React, { useState } from "react";
import { generatePreviewPDF } from "./generatePreviewPDF";

export default function PreviewMode({ formData, onUpgrade }) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Nach oben scrollen beim Laden
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handlePreviewPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await generatePreviewPDF(formData);
    } catch (error) {
      console.error('Fehler beim PDF-Generieren:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleCSVExport = () => {
    const csvContent = Object.entries(formData)
      .map(([key, value]) => `${key},${JSON.stringify(value)}`)
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "verfahrensdokumentation_daten.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEmailContact = () => {
    const subject = "Verfahrensdokumentation";
    const body = "Sehr geehrter Herr Zilker,\n\nich möchte gerne die vollständige GOBD-Dokumentation für mein Unternehmen XY erwerben. Bitte senden Sie mir hierzu die Zugangsinformationen.\n\nMit freundlichen Grüßen";
    const mailtoLink = `mailto:kanzlei@hgz-stb.de?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
          🎉 Preview Ihrer GOBD-Dokumentation
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Sie haben die ersten 3 Schritte erfolgreich abgeschlossen
        </Typography>
      </Box>

      {/* Preview Status */}
      <Alert severity="success" sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          ✅ Preview erfolgreich erstellt!
        </Typography>
        <Typography>
          Sie haben die Grundinformationen zu Ihrem Unternehmen, der Organisation und den relevanten Unterlagen erfasst. 
          Hier ist eine Vorschau Ihrer bisherigen Eingaben:
        </Typography>
      </Alert>

      {/* Preview Content */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: "center" }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <Description color="primary" />
                Unternehmensinformationen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Firmenname:</strong> {formData.companyName || "Nicht angegeben"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Standort:</strong> {formData.location || "Nicht angegeben"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Rechtsform:</strong> {formData.legalForm || "Nicht angegeben"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Branche:</strong> {formData.specialIndustryFeatures || "Nicht angegeben"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <CheckCircle color="success" />
                Organisationseinheiten
              </Typography>
              {formData.organizationUnits && formData.organizationUnits.length > 0 ? (
                formData.organizationUnits.slice(0, 3).map((unit, index) => (
                  <Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>{unit.unit}:</strong> {Array.isArray(unit.processStep) ? unit.processStep.join(', ') : unit.processStep}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Keine Organisationseinheiten erfasst
                </Typography>
              )}
              {formData.organizationUnits && formData.organizationUnits.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  ... und {formData.organizationUnits.length - 3} weitere
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview PDF Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 5, 
          mb: 4, 
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
          }
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              mb: 2, 
              fontWeight: "700",
              background: "linear-gradient(45deg, #1976d2, #42a5f5)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1
            }}
          >
            📄 Preview-PDF mit Wasserzeichen
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.6
            }}
          >
            Laden Sie eine Vorschau Ihrer Dokumentation als PDF herunter
          </Typography>
        </Box>

        <Box sx={{ 
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          mb: 4,
          maxWidth: "900px",
          mx: "auto"
        }}>
          <Box sx={{ 
            textAlign: "center",
            p: 3,
            borderRadius: 2,
            background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            height: "180px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Description sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: "600", color: "primary.main", mb: 1 }}>
              Erste 3 Kapitel
            </Typography>
            <Typography variant="body2" color="primary.dark">
              Ihre bisherigen Eingaben als strukturierte Dokumentation
            </Typography>
          </Box>
          
          <Box sx={{ 
            textAlign: "center",
            p: 3,
            borderRadius: 2,
            background: "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)",
            height: "180px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <CheckCircle sx={{ fontSize: 48, color: "secondary.main", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: "600", color: "secondary.main", mb: 1 }}>
              Finale Formatierung
            </Typography>
            <Typography variant="body2" color="secondary.dark">
              Struktur und Design der vollständigen Dokumentation
            </Typography>
          </Box>
          
          <Box sx={{ 
            textAlign: "center",
            p: 3,
            borderRadius: 2,
            background: "linear-gradient(135deg, #fff3e0 0%, #ffcc02 100%)",
            height: "180px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center"
          }}>
            <Lock sx={{ fontSize: 48, color: "warning.main", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: "600", color: "warning.main", mb: 1 }}>
              Preview-Wasserzeichen
            </Typography>
            <Typography variant="body2" color="warning.dark">
              Kennzeichnung als Vorschau-Version
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: "flex", 
          gap: 3, 
          justifyContent: "center", 
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <Button
            variant="contained"
            size="large"
            onClick={handlePreviewPDF}
            disabled={isGeneratingPDF}
            sx={{ 
              px: 5, 
              py: 2, 
              fontSize: "1.2rem",
              fontWeight: "600",
              borderRadius: 3,
              background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
              boxShadow: "0 6px 20px rgba(25, 118, 210, .3)",
              "&:hover": {
                background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(25, 118, 210, .4)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            {isGeneratingPDF ? "PDF wird erstellt..." : "📥 Preview-PDF herunterladen"}
          </Button>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleCSVExport}
            startIcon={<FileDownload />}
            sx={{ 
              px: 5, 
              py: 2, 
              fontSize: "1.2rem",
              fontWeight: "600",
              borderRadius: 3,
              background: "linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)",
              boxShadow: "0 6px 20px rgba(255, 152, 0, .3)",
              border: "2px solid #f57c00",
              "&:hover": {
                background: "linear-gradient(45deg, #f57c00 30%, #ff9800 90%)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(255, 152, 0, .4)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            📊 Daten Export (WICHTIG!)
          </Button>
        </Box>
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Important Export Warning */}
      <Alert 
        severity="warning" 
        sx={{ 
          mb: 4,
          p: 3,
          borderRadius: 2,
          border: "2px solid #ff9800",
          backgroundColor: "#fff3e0",
          "& .MuiAlert-icon": {
            fontSize: "2rem"
          }
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", color: "#e65100" }}>
          ⚠️ WICHTIGER HINWEIS: Daten exportieren!
        </Typography>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, fontSize: "1.1rem" }}>
          <strong>Bevor Sie zur vollständigen Dokumentation wechseln:</strong>
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
            <strong>Exportieren Sie unbedingt Ihre eingegebenen Daten</strong> über den "Daten Export" Button oben
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.6 }}>
            In der Vollversion können Sie diese Daten wieder hochladen und <strong>bei Schritt 4 weitermachen</strong>
          </Typography>
          <Typography component="li" variant="body1" sx={{ lineHeight: 1.6 }}>
            Ohne Export müssen Sie alle Schritte 1-3 erneut eingeben
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ 
          fontStyle: "italic", 
          color: "#bf360c",
          fontWeight: "500",
          mt: 2,
          p: 2,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          borderRadius: 1,
          border: "1px solid #ffcc02"
        }}>
          💡 Tipp: Speichern Sie die CSV-Datei an einem sicheren Ort, damit Sie sie später wiederfinden!
        </Typography>
      </Alert>

      {/* Upgrade Section */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: "center", fontWeight: "bold", mb: 3 }}>
          🚀 Vollständige GOBD-Dokumentation erstellen
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 4, justifyContent: "center" }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", textAlign: "center" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <Description color="primary" />
                  Vollständige Dokumentation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Alle 14 Schritte der GOBD-Dokumentation mit detaillierten Verfahrensbeschreibungen
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", textAlign: "center" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CheckCircle color="success" />
                  Professionelles PDF
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hochwertiges PDF ohne Wasserzeichen, direkt einsatzbereit für Behörden und Prüfer
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", textAlign: "center" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <Lock color="warning" />
                  Rechtssicherheit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  GoBD-konforme Dokumentation nach AWV-Standard für maximale Rechtssicherheit
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleEmailContact}
            sx={{ 
              px: 6, 
              py: 2, 
              fontSize: "1.2rem",
              background: "linear-gradient(45deg, #2e7d32 30%, #4caf50 90%)",
              boxShadow: "0 4px 8px 2px rgba(46, 125, 50, .3)",
              "&:hover": {
                background: "linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)",
              }
            }}
          >
            <Payment sx={{ mr: 1 }} />
            Zugang für vollständige Dokumentation anfragen
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
